import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import Joi from 'joi';

import {
    useGetInvestmentFundsQuery,
    useGetPromotersQuery,
    useGetRolesQuery,
    useGetUsersQuery,
    useUpdateDistributorCommercialMutation,
    useUpdateDistributorCompanyMutation,
} from 'api/main';
import { corporateForms } from 'utils/formData';
import {
    conventionStatus,
    distributorType,
    EURO_SYMBOL,
    ORDER_BY,
    roleCategories,
    RoleNames,
    sortDirection,
} from 'utils/constants';
import {
    convertToBoolOrUndefined,
    jsonParseSafe,
    parseDate,
    parseFullName,
    parseSum,
    purgeEmptyFields,
    validateSchema,
} from 'utils/helpers';
import { texts } from 'utils/languages/fr';

import InfoRow from './InfoRow';
import InfoBox from './InfoBox';
import SectionActions from './SectionActions';
import CompanyLogo from 'containers/subscriptions/CompanyLogo';
import {
    CustomDatePicker,
    CustomSelect,
    CustomTextField,
    BasicAddressForm,
    Loader,
    RadioLabel,
    TransferList,
    ActionButton,
} from 'components';
import {
    Box,
    Checkbox,
    IconButton,
    InputAdornment,
    MenuItem,
    RadioGroup,
    Typography,
} from '@mui/material';
import * as icons from 'mdi-material-ui';
import { userSchemas } from 'schemas';
import { useMatchAtLeastOneRole, useUserRoleMatchCategory } from '../../../utils/hooks';

const parseOrganization = org => {
    if (typeof org !== 'boolean') return '-';
    return org ? texts.RESEAU : texts.INDEPENDANT;
};

const DistributorComplyAdvantageStatus = props => {
    const { status } = props;
    if (!status) {
        return <div>-</div>;
    }
    const info = [];
    if (status?.pep_user) {
        info.push(texts.PEP_DISTRIBUTOR_USER);
    }
    if (status?.sanction_user) {
        info.push(texts.SANCTIONS_DISTRIBUTOR_USER);
    }
    if (status?.pep_company) {
        info.push(texts.PEP_COMPANY);
    }
    if (status?.sanction_company) {
        info.push(texts.SANCTIONS_COMPANY);
    }
    if (status?.pep_legal_representative) {
        info.push(texts.PEP_DISTRIBUTOR_REPRESENTATIVE);
    }
    if (status?.sanction_legal_representative) {
        info.push(texts.SANCTIONS_DISTRIBUTOR_REPRESENTATIVE);
    }

    return (
        <div>
            {info.map(message => (
                <div key={message}>- {message}</div>
            ))}
        </div>
    );
};

DistributorComplyAdvantageStatus.propTypes = { status: PropTypes.object };

const getDefaultForm = () => ({
    corporateForm: '',
    organization: '',
    shareCapital: '',
    rcsNumber: '',
    rcsRegistrationAddress: '',
    rcsRegistrationDate: '',
    vatNumber: '',
    oriasNumber: '',
    acprNumber: '',
    currentLocation: {
        formattedAddress: '',
    },
});

const schema = {
    rcsRegistrationAddress: Joi.string()
        .trim()
        .pattern(/^[ a-zA-ZÀ-ÿ]+$/)
        .allow('')
        .messages({ 'string.pattern.base': texts.RCS_REGISTRATION_ADDRESS_ONLY_LETTERS }),
    oriasNumber: Joi.string().length(8).pattern(/^\d+$/).allow('').messages({
        'string.length': texts.ORIAS_NUMBER_LENGTH,
        'string.pattern.base': texts.ORIAS_NUMBER_LENGTH,
    }),
};

const CompanySection = props => {
    const { distributor, canEditMissingInfo, complyAdvantageStatus } = props;

    const subscription = distributor?.user?.subscriptions?.[0];
    const isAdminCIF = subscription?.role?.name === RoleNames.ADMIN_CIF;
    const isPSI = subscription?.role?.name === RoleNames.PSI;
    const userIsServicerAdmin = useMatchAtLeastOneRole([
        RoleNames.SERVICER_ADMIN,
        RoleNames.SERVICER_SIGNATORY,
    ]);
    const userIsPromoter = useUserRoleMatchCategory(roleCategories.PROMOTERS);
    const userIsAdminPromoter = useMatchAtLeastOneRole([RoleNames.PROMOTER_ADMIN]);

    const [form, setForm] = useState(getDefaultForm());
    const [errors, setErrors] = useState({});
    const [promoterCommercial, setPromoterCommercial] = useState('');
    const [selectedPromoters, setSelectedPromoters] = useState(distributor?.promoters || []);

    const { data: investmentFunds = [] } = useGetInvestmentFundsQuery();
    const { data: { promoters } = { promoters: [] }, isLoading: promotersLoading } =
        useGetPromotersQuery({ pagination: false }, { skip: !userIsServicerAdmin });
    const [updateDistributorCompany, { isLoading: loading }] =
        useUpdateDistributorCompanyMutation();
    const [updateDistributorCommercial, { isLoading: commercialUpdating }] =
        useUpdateDistributorCommercialMutation();
    const { data: roles = [], isLoading: rolesLoader } = useGetRolesQuery();
    const { data: { userList: promoterUsers = [] } = { userList: [] }, isFetching: listLoader } =
        useGetUsersQuery(
            userSchemas.getUsers({
                disablePagination: true,
                orderBy: ORDER_BY.lastName,
                direction: sortDirection.ASC,
                role: roles
                    .filter(r => r.category?.name === roleCategories.PROMOTERS)
                    .map(r => r.id),
            }),
            { skip: !(roles.length && userIsPromoter) }
        );

    const productsWithConventions = useMemo(
        () =>
            investmentFunds.filter(
                p =>
                    distributor.conventions.find(c => c.investmentFund.id === p.id)?.status ===
                    conventionStatus.FULLY_SIGNED
            ),
        [distributor, investmentFunds]
    );

    // promoters that are associated with funds that the distributor has signed a convention for
    const permanentPromoters = useMemo(
        () =>
            distributor?.promoters?.filter(p =>
                productsWithConventions.find(pc => pc.promoter.id === p.id)
            ),
        [distributor, productsWithConventions]
    );

    const handleChange = useCallback(
        key => e => {
            setForm(prevState => ({ ...prevState, [key]: e.target.value }));
        },
        []
    );

    const handleAddressChange = useCallback(fullAddress => {
        setForm(prevState => ({ ...prevState, currentLocation: fullAddress }));
    }, []);

    const handleDateChange = useCallback(
        newDate => setForm(prevState => ({ ...prevState, rcsRegistrationDate: newDate })),
        []
    );

    const handleChangePromoters = useCallback(newValue => {
        setSelectedPromoters(newValue);
    }, []);

    const handleSavePromoterCommercial = useCallback(() => {
        updateDistributorCommercial({
            id: distributor.id,
            promoterCommercial,
        });
    }, [updateDistributorCommercial, distributor, promoterCommercial]);

    const handleSaveValidateSubscriptions = useCallback(() => {
        updateDistributorCompany({
            payload: {
                id: distributor.id,
                validateSubscriptions: !distributor?.validateSubscriptions,
            },
        });
    }, [updateDistributorCompany, distributor]);

    const handleUpdatePromoters = useCallback(() => {
        if (selectedPromoters.length) {
            updateDistributorCompany({
                payload: {
                    id: distributor.id,
                    promoters: selectedPromoters.map(p => `/api/promoters/${p.id}`),
                },
            });
        } else {
            setErrors(prevState => ({ ...prevState, promoters: texts.AT_LEAST_ONE_PROMOTER }));
        }
    }, [updateDistributorCompany, distributor.id, selectedPromoters]);

    const handleCancel = useCallback(() => setForm(getDefaultForm()), []);

    const handleSubmit = useCallback(() => {
        const { valid, newErrors } = validateSchema(schema, form);
        if (valid) {
            setErrors({});
            updateDistributorCompany({
                payload: {
                    id: distributor.id,
                    ...purgeEmptyFields(form),
                    shareCapital: +form.shareCapital || undefined,
                    organization: convertToBoolOrUndefined(form.organization),
                    corporateForm: form.corporateForm
                        ? JSON.stringify({ choice: form.corporateForm })
                        : undefined,
                },
                callback: () => {
                    setForm(getDefaultForm());
                },
            });
        } else {
            setErrors(newErrors);
        }
    }, [form, distributor, updateDistributorCompany]);

    const hasMissingInfo =
        !jsonParseSafe(distributor?.corporateForm)?.choice ||
        typeof distributor?.organization !== 'boolean' ||
        !distributor?.shareCapital ||
        !distributor?.rcsNumber ||
        !distributor?.rcsRegistrationAddress ||
        !distributor?.rcsRegistrationDate ||
        !distributor?.vatNumber ||
        (isAdminCIF && !distributor?.oriasNumber) ||
        (isPSI && !distributor?.acprNumber) ||
        !distributor?.currentLocation?.formattedAddress;

    useEffect(() => {
        setSelectedPromoters(distributor?.promoters || []);
    }, [distributor?.promoters]);

    useEffect(() => {
        if (userIsPromoter) {
            const availableIds = promoterUsers.map(p => p.id);
            const promoterCommercialObject =
                distributor?.managedByUsers.find(m => availableIds.includes(m.id)) ?? null;
            if (promoterCommercialObject?.id) {
                setPromoterCommercial(promoterCommercialObject.id);
            }
        }
    }, [distributor?.managedByUsers, userIsPromoter, promoterUsers]);

    return (
        <InfoBox
            leftColumnContent={
                <CompanyLogo
                    key={distributor?.companyLogo?.name}
                    id={distributor?.companyLogo?.id}
                    url={distributor?.companyLogo?.url}
                    width="100%"
                />
            }
        >
            {userIsPromoter ? (
                <InfoRow
                    mb={2}
                    title={texts.DISTRIBUTOR_SERVICER}
                    data={
                        <Loader loading={rolesLoader || listLoader} marginTop={1}>
                            <Box display="flex" alignItems="center">
                                <CustomSelect
                                    fullWidth
                                    controlSx={{ marginBottom: 0 }}
                                    value={promoterCommercial}
                                    disabled={!userIsAdminPromoter}
                                    onChange={e => setPromoterCommercial(e.target.value)}
                                >
                                    {promoterUsers.map(user => (
                                        <MenuItem key={user.id} value={user.id}>
                                            {parseFullName(user)}
                                        </MenuItem>
                                    ))}
                                </CustomSelect>
                                {userIsAdminPromoter && (
                                    <IconButton
                                        disableRipple
                                        color="primary"
                                        sx={{
                                            color: 'grey.500',
                                            '&:hover': { color: 'primary.main' },
                                        }}
                                        onClick={handleSavePromoterCommercial}
                                        disabled={commercialUpdating || !promoterCommercial}
                                    >
                                        <icons.ContentSave />
                                    </IconButton>
                                )}
                            </Box>
                        </Loader>
                    }
                />
            ) : (
                <InfoRow
                    mb={2}
                    title={texts.DISTRIBUTOR_SERVICER}
                    data={distributor?.managedByUsers?.map(u => parseFullName(u)).join(', ')}
                />
            )}
            <InfoRow
                title={texts.TYPE}
                data={
                    Object.keys(distributorType).find(
                        k => distributorType[k] === distributor?.type
                    ) || '-'
                }
            />
            <InfoRow
                title={texts.NETWORKS}
                data={distributor?.networks?.map(network => network.name).join(', ') || '-'}
            />
            <InfoRow title="CIF / PSI" data={isPSI ? 'PSI' : 'CIF'} />
            {userIsServicerAdmin && (
                <InfoRow
                    title={texts.VERIFICATION}
                    data={
                        <Checkbox
                            checked={distributor?.validateSubscriptions}
                            tabIndex={-1}
                            onChange={handleSaveValidateSubscriptions}
                            disableRipple
                            sx={{ padding: 0 }}
                        />
                    }
                />
            )}
            <InfoRow
                title={texts.FORME_SOCIALE}
                data={jsonParseSafe(distributor?.corporateForm)?.choice || '-'}
            >
                {canEditMissingInfo && (
                    <CustomSelect
                        value={form.corporateForm}
                        onChange={handleChange('corporateForm')}
                        error={errors.corporateForm}
                    >
                        {Object.values(corporateForms).map(corporateForm => (
                            <MenuItem key={corporateForm} value={corporateForm}>
                                {corporateForm}
                            </MenuItem>
                        ))}
                    </CustomSelect>
                )}
            </InfoRow>
            <InfoRow title={texts.ORGANISATION} data={parseOrganization(distributor?.organization)}>
                {canEditMissingInfo && (
                    <RadioGroup value={form.organization} onChange={handleChange('organization')}>
                        <RadioLabel value="true" label={texts.RESEAU} />
                        <RadioLabel value="false" label={texts.INDEPENDANT} />
                    </RadioGroup>
                )}
            </InfoRow>
            {!!subscription?.finishedSubscriptionAt && (
                <InfoRow
                    title={texts.DATE_ENTREE}
                    data={parseDate(subscription?.finishedSubscriptionAt) || '-'}
                />
            )}
            <InfoRow
                title={texts.CAPITAL_SOCIAL}
                data={distributor?.shareCapital ? parseSum(distributor?.shareCapital) : '-'}
            >
                {canEditMissingInfo && (
                    <CustomTextField
                        value={form.shareCapital}
                        onChange={handleChange('shareCapital')}
                        endAdornment={
                            <InputAdornment position="start">{EURO_SYMBOL}</InputAdornment>
                        }
                        formatNumber
                    />
                )}
            </InfoRow>
            <InfoRow title="SIREN / RCS" data={distributor?.rcsNumber || '-'}>
                {canEditMissingInfo && (
                    <CustomTextField value={form.rcsNumber} onChange={handleChange('rcsNumber')} />
                )}
            </InfoRow>
            <InfoRow title={texts.RCS_ADDRESS} data={distributor?.rcsRegistrationAddress || '-'}>
                {canEditMissingInfo && (
                    <CustomTextField
                        value={form.rcsRegistrationAddress}
                        onChange={handleChange('rcsRegistrationAddress')}
                        error={errors.rcsRegistrationAddress}
                    />
                )}
            </InfoRow>
            <InfoRow
                title={texts.RCS_DATE}
                data={parseDate(distributor?.rcsRegistrationDate) || '-'}
            >
                {canEditMissingInfo && (
                    <CustomDatePicker
                        disableFuture
                        sx={{ backgroundColor: 'white' }}
                        value={form.rcsRegistrationDate}
                        onChange={handleDateChange}
                    />
                )}
            </InfoRow>
            <InfoRow title={texts.VAT_NUMBER} data={distributor?.vatNumber || '-'}>
                {canEditMissingInfo && (
                    <CustomTextField
                        value={form.vatNumber}
                        onChange={handleChange('vatNumber')}
                        showError={!!errors.vatNumber}
                    />
                )}
            </InfoRow>
            {isAdminCIF && (
                <InfoRow title={texts.ORIAS_NUMBER} data={distributor?.oriasNumber || '-'}>
                    {canEditMissingInfo && (
                        <CustomTextField
                            type="number"
                            value={form.oriasNumber}
                            onChange={handleChange('oriasNumber')}
                            error={errors.oriasNumber}
                            showError={!!errors.oriasNumber}
                        />
                    )}
                </InfoRow>
            )}
            {isPSI && (
                <InfoRow title={texts.ACPR_NUMBER} data={distributor?.acprNumber || '-'}>
                    {canEditMissingInfo && (
                        <CustomTextField
                            value={form.acprNumber}
                            onChange={handleChange('acprNumber')}
                        />
                    )}
                </InfoRow>
            )}
            <InfoRow
                title={texts.SIEGE_SOCIAL}
                data={distributor?.currentLocation?.formattedAddress || '-'}
            >
                {canEditMissingInfo && (
                    <BasicAddressForm
                        useFullAddress
                        address={form.currentLocation.formattedAddress}
                        onAddressChange={handleAddressChange}
                    />
                )}
            </InfoRow>
            <InfoRow
                title={texts.COMPLY_ADVANTAGE_STATUS}
                data={<DistributorComplyAdvantageStatus status={complyAdvantageStatus} />}
            />

            {userIsServicerAdmin ? (
                <React.Fragment>
                    <TransferList
                        permanentItems={permanentPromoters}
                        items={promoters}
                        value={selectedPromoters}
                        label={
                            <Typography fontWeight="600" component="span">
                                {texts.PROMOTER}
                                {'\xa0'}:
                            </Typography>
                        }
                        textKey="name"
                        onChange={handleChangePromoters}
                        loading={promotersLoading || loading}
                        marginY={0}
                        error={errors.promoters}
                    />
                    <Box textAlign="center" width="100%" paddingY={2}>
                        <ActionButton
                            variant="contained"
                            onClick={handleUpdatePromoters}
                            loading={loading}
                        >
                            {texts.SAVE}
                        </ActionButton>
                    </Box>
                </React.Fragment>
            ) : (
                <InfoRow
                    title={texts.PROMOTERS}
                    data={distributor?.promoters?.map(p => p.name).join(', ')}
                />
            )}
            {hasMissingInfo && canEditMissingInfo && (
                <SectionActions
                    loading={loading}
                    handleCancel={handleCancel}
                    handleSubmit={handleSubmit}
                />
            )}
        </InfoBox>
    );
};

CompanySection.propTypes = {
    distributor: PropTypes.object,
    canEditMissingInfo: PropTypes.bool,
    complyAdvantageStatus: PropTypes.object,
};

export default CompanySection;
